import { Subscription } from '../../components/account'
import { ReleaseProduct } from '../../components/release'
import { CLIENT_PLATFORM, SUBSCRIPTION_STATUS } from '../../constants/enums'
import { BaseSDK } from '../../sdk'

import { uuid } from '../../utils'
import { ACCESS_TYPE } from './enums'
import { ClientData, SourceData, SubscriptionMeta } from './types'
import { VIDEO_TYPE } from './video/enums'

export class TelemetrySession<T> {
  #uuid: string
  #userId: string | number
  #subscription!: SubscriptionMeta
  #client: ClientData
  #source?: SourceData
  #content: T
  #accessType: ACCESS_TYPE

  static get className() {
    return 'TelemetrySession'
  }

  get id() {
    return this.#uuid
  }

  get subscription() {
    return this.#subscription
  }

  set subscription(value: SubscriptionMeta | Subscription) {
    try {
      const className = 'Subscription'

      if (value instanceof Subscription || (value?.constructor['className' as never] === className || value?.constructor.name === className)) {
        const subscription = value as Subscription
        
        this.#subscription = {
          id: String(value.id),
          status: subscription.data?.status ? subscription.data?.status as string : SUBSCRIPTION_STATUS.UNDEFINED,
          plan: {
            id: value.plan
          }
        }
      } else this.#subscription = value
    } catch {
      this.#subscription = value as SubscriptionMeta
    }
  }

  get accessType() {
    return this.#accessType
  }

  set accessType(value: ACCESS_TYPE) {
    this.#accessType = value
  }

  get data() {
    return {
      user: {
        id: this.#userId,
        subscription: this.subscription
      },
      client: this.#client,
      content: this.#content,
      accessType: this.accessType,
      source: this.#source,
      playbackId: this.#uuid
    }
  }

  set userId(value: string) {
    this.#userId = value
  }

  set source(value: SourceData) {
    this.#source = value
  }

  set client(value: ClientData) {
    this.#client = value
  }

  set content(value: T) {
    this.#content = value
  }

  constructor(userId: string | number, subscription: Subscription | SubscriptionMeta, client: ClientData, content: T, accessType: ACCESS_TYPE, source: SourceData) {
    this.#uuid = uuid()
    this.#userId = userId
    this.subscription = subscription
    this.#client = client
    this.#content = content
    this.#accessType = accessType
    this.#source = source
  }
}

export default function createTelemetrySession<T>(sdk: BaseSDK, release: ReleaseProduct, source: SourceData): TelemetrySession<T> {
  const meta = {
    name: location.host,
    platform: CLIENT_PLATFORM.WEB,
    version: sdk.version
  }

  const content = {
    videoType: (release.isVOD || release.isSVOD) && !release.stream.isLive ? VIDEO_TYPE.VOD : VIDEO_TYPE.LIVESTREAM,
    releaseId: String(release.id),
    productId: String(release.productId)
  } as T

  const type = release.isBySubscription ?
    ACCESS_TYPE.SUBSCRIPTION :
    release.isPurchased ? ACCESS_TYPE.PURCHASED : ACCESS_TYPE.FREE

  const modifiedSource = {
    artistId: String(source.artistId ? source.artistId : release.artist.id),
    page: source.page,
    releaseId: String(source.releaseId ? source.releaseId : release.id),
    section: source.section
  }

  // User started video playback from scratch / or after video end
  return new TelemetrySession<T>(sdk.account.id, sdk.account.subscription, meta, content, type, modifiedSource)
}
